import { MAX_RECORDING_BYTES, MAX_RECORDING_SECONDS, RECORDING_MIME_TYPES, recordingMimeType } from '../../shared/recording.js';

export class AnswerRecorder {
  constructor({ getUserMedia = constraints => navigator.mediaDevices.getUserMedia(constraints), Recorder = globalThis.MediaRecorder, onState = () => {}, onReady = () => {}, onError = () => {}, schedule = (fn,ms) => setTimeout(fn,ms), cancel = timer => clearTimeout(timer) } = {}) {
    Object.assign(this, { getUserMedia, Recorder, onState, onReady, onError, schedule, cancel });
    this.state = 'idle'; this.epoch = 0;
  }
  change(state) { this.state = state; this.onState(state); }
  async start() {
    if (['requesting', 'recording', 'stopping'].includes(this.state)) return;
    if (!this.Recorder) throw new Error('Recording is unavailable in this browser. Try Safari or Chrome.');
    const epoch = ++this.epoch;
    this.change('requesting');
    try {
      const stream = await this.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      if (epoch !== this.epoch) { stream.getTracks().forEach(track => track.stop()); return; }
      this.stream = stream;
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm', 'audio/ogg;codecs=opus'].find(type => this.Recorder.isTypeSupported?.(type));
      const recorder = this.recorder = new this.Recorder(stream, { ...(mimeType ? { mimeType } : {}), audioBitsPerSecond: 64000 });
      this.chunks = []; this.byteCount = 0; this.started = Date.now();
      recorder.ondataavailable = event => { if (event.data?.size && epoch === this.epoch) { this.chunks.push(event.data); this.byteCount += event.data.size; if (this.byteCount > MAX_RECORDING_BYTES) this.stop(); } };
      recorder.onerror = () => { this.dispose(); this.change('idle'); this.onError(new Error('Your recording could not be checked. Please record it again.')); };
      recorder.onstop = () => {
        this.release();
        if (epoch !== this.epoch) return;
        const blob = new Blob(this.chunks, { type: recorder.mimeType || mimeType });
        this.chunks = [];
        if (!blob.size || blob.size > MAX_RECORDING_BYTES || !RECORDING_MIME_TYPES.includes(recordingMimeType(blob.type))) { this.change('idle'); this.onError(new Error('Record a short answer and try again.')); return; }
        this.change('ready');
        this.onReady({ blob, durationMs: Date.now() - this.started });
      };
      recorder.start(1000);
      this.change('recording');
      this.timer = this.schedule(() => this.stop(), MAX_RECORDING_SECONDS * 1000);
    } catch (error) {
      if (epoch !== this.epoch) return;
      this.release(); this.change('idle');
      if (error?.name === 'NotAllowedError') throw new Error('Allow microphone access in your browser, then try again.');
      if (error?.name === 'NotFoundError') throw new Error('No microphone was found. Check your microphone and try again.');
      throw error;
    }
  }
  stop() {
    if (this.recorder?.state !== 'recording') return;
    this.change('stopping'); this.cancel(this.timer); this.recorder.stop();
  }
  release() { this.cancel(this.timer); this.stream?.getTracks().forEach(track => track.stop()); this.stream = null; }
  dispose() {
    ++this.epoch;
    if (this.recorder?.state === 'recording') this.recorder.stop();
    this.release(); this.chunks = []; this.recorder = null; this.state = 'idle';
  }
}

export async function recordingPayload(blob) {
  if (!blob?.size || blob.size > MAX_RECORDING_BYTES) throw new Error('Record a short answer and try again.');
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 32768) binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
  return { mimeType: recordingMimeType(blob.type), data: btoa(binary) };
}
